import { CommonModule } from '@angular/common';
import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ListTableComponent } from '@ppl/ui';
import { DatePickerModule } from 'primeng/datepicker';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { PopupDialogComponent } from '@ppl/ui';
import { ButtonComponent } from '@ppl/ui';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { TimesheetService } from '../../../../services/timesheet.service';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { environment } from '../../../../../environments/environment';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { DateTimeFormatterService } from 'src/app/shared/utils/date-time-formatter.service';
import { ErrorAlertService } from 'src/app/shared/services/error-alert.service';
import { ListService } from 'src/app/shared/services/list.service';
import { LookupService } from 'src/app/services/lookup.service';
import { UserService } from 'src/app/services/user.service';
import { TableRowActions } from 'src/app/shared/interfaces/actions';
import { TableRowActionsModel } from 'src/app/shared/interfaces/list.interface';
import { LookupValueItem } from 'src/app/interfaces/lookup.interface';
import { PurchaseAdjustmentFilterComponent } from '../purchase-filter/purchase-filter.component';
import { PurchaseRequestService } from 'src/app/services/purchase-request.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { AuthorizationDetailsComponent } from '../../authorizations/authorization-details/authorization-details.component';
import { SectionsService } from '../../../../services/sections.service';
import { CreatePurchaseRequestComponent } from '../create-purchase-request/create-purchase-request.component';

@Component({
  selector: 'ppl-purchase-request-payroll-adjustment-approvals',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgZorroModule,
    NzDatePickerModule,
    DatePickerModule,
    ButtonComponent,
    PurchaseAdjustmentFilterComponent,
    PopupDialogComponent,
    ListTableComponent,
    AuthorizationDetailsComponent,
    CreatePurchaseRequestComponent
  ],
  templateUrl: './purchase-request-payroll-adjustment-approvals.component.html',
  styleUrl: './purchase-request-payroll-adjustment-approvals.component.css'
})
export class PurchaseRequestPayrollAdjustmentApprovalsComponent {
  @Input() source: string = 'purchase request';
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild(PurchaseAdjustmentFilterComponent) timesheetFilterChildComponent!: PurchaseAdjustmentFilterComponent;
  @ViewChild('searchFilterWarningDialog') searchFilterWarningDialog!: TemplateRef<any>;
  @ViewChild('authDialog') authDialog!: TemplateRef<any>;
  // Passed to ppl-authorization-details as an input; never put into the shared selected lead.
  selectedAuthorizationId: any = null;
  @ViewChild('viewPurchaseRequestDialog') viewPurchaseRequestDialog!: TemplateRef<any>;
  @ViewChild('originalRequestDialog') originalRequestDialog!: TemplateRef<any>;

  private dialogSvc = inject(DialogService);
  private allowListStore = inject(AllowListAuthStore);

  pageId = PAGE_CONSTANTS.STAFF_PURCHASE_REQUEST_APPROVALS;
  isReadOnly = false;
  isPageRestrictionLoaded = false;

  actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
  mode: 'add' | 'edit' | 'view' | 'actions' = 'view';
  viewPurchaseRequestData: any;
  viewPurchaseRequestDialogId: any;
  originalRequestDialogId: any;
  originalRequestLines: any[] = [];
  originalRequestLoaded = false;
  appliedFilters: any = [];
  approvalTimesheetForm!: FormGroup;
  dialogButtonLabel = '';
  commentText = '';
  dialogData: any;
  dialogNoteMessage = '';
  isDialogNoteDisplayed = false;
  programName!: string;
  processingStatusList: any;
  reasonList: any;
  businessStatusList: any;
  selectedAction: any;
  serviceCodes: LookupValueItem[] = [];
  showActionPopup: boolean = false;
  timesheets: any[] = [];
  hasSearched = false;
  title = '';
  chequeNumbers: any[] = [];

  buttons = [
    {
      button_id: "Approve",
      button_label: "Approve",
      buttonAction: "approveTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "ApproveAll",
      button_label: "Approve All",
      buttonAction: "approveAllTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "Reject",
      button_label: "Reject",
      buttonAction: "rejectTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "RejectAll",
      button_label: "Reject All",
      buttonAction: "rejectAllTimesheets",
      className: "primary-btn-color"
    }
  ];

  cols = [
    { field: 'pplPurchaseId', header: 'PPL Purchase \nID', jsonpath: 'pplPurchaseId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'submissionDate', header: 'Submission \nDate', jsonpath: 'submissionDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'consumerName', header: 'Participant \nName', jsonpath: 'consumerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'participantPplId', header: 'Participant \nPPL ID', jsonpath: 'participantPplId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
    { field: 'vendorName', header: 'Vendor \nName', jsonpath: 'vendorName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'serviceCode', header: 'Service \nCode', jsonpath: 'serviceCode', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'authDesc', header: 'Service \nDescription', jsonpath: 'authDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'itemDesc', header: 'Item \nDescription', jsonpath: 'itemDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'unit', header: 'Units', jsonpath: 'unit', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'price', header: 'Price', jsonpath: 'price', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'taxAmount', header: 'Tax \nAmount', jsonpath: 'taxAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'shippingAmount', header: 'Shipping \nAmount', jsonpath: 'shippingAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'totalAmount', header: 'Total \nAmount', jsonpath: 'totalAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'businessStatus', header: 'Business \nStatus', jsonpath: 'businessStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'processingStatus', header: 'Processing \nStatus', jsonpath: 'processingStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'orderNumber', header: 'Order \nNumber', jsonpath: 'orderNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'trackingNumber', header: 'Tracking \nNumber', jsonpath: 'trackingNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'tasAuthorizationDetailId', header: 'Authorization \nNumber', jsonpath: 'tasAuthorizationDetailId', isDisplayedAtHeader: 'y', isClickable: true, isSearchable: 'n' },
    { field: 'viewOriginal', header: 'Original \nRequest', jsonpath: 'viewOriginal', isDisplayedAtHeader: 'y', isClickable: true, isSearchable: 'n' },
    // { field: 'sourceTypeLkp', header: 'Source', jsonpath: 'sourceTypeLkp', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  originalCols = [
    { field: 'pplPurchaseId', header: 'PPL Purchase \nID', jsonpath: 'pplPurchaseId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'consumerName', header: 'Participant \nName', jsonpath: 'consumerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'vendorName', header: 'Vendor \nName', jsonpath: 'vendorName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'mcoName', header: 'MCO \nName', jsonpath: 'mcoName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'service', header: 'Service', jsonpath: 'service', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'taxAmount', header: 'Tax \nAmount', jsonpath: 'taxAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'shippingAmount', header: 'Shipping \nAmount', jsonpath: 'shippingAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'totalAmount', header: 'Total \nAmount', jsonpath: 'totalAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'businessStatus', header: 'Business \nStatus', jsonpath: 'businessStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'processingStatus', header: 'Processing \nStatus', jsonpath: 'processingStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  // lineItemCols = [
  //   {
  //     field: 'createdDate',
  //     header: 'Date',
  //     jsonpath: 'createdDate',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'authDesc',
  //     header: 'Service Description',
  //     jsonpath: 'authDesc',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'consumerName',
  //     header: 'Participant Name',
  //     jsonpath: 'consumerName',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'y',
  //   },
  //   {
  //     field: 'consumerPplId',
  //     header: 'Participant PPL ID',
  //     jsonpath: 'consumerPplId',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'providerName',
  //     header: 'Provider Name',
  //     jsonpath: 'providerName',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'providerPplId',
  //     header: 'Provider PPL ID',
  //     jsonpath: 'providerPplId',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },

  //   {
  //     field: 'timeInTimeOut',
  //     header: 'Time In - Time Out',
  //     jsonpath: 'timeInTimeOut',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'billedUnits',
  //     header: 'Billed Units (hrs)',
  //     jsonpath: 'billedUnits',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'paidUnits',
  //     header: 'Paid Units',
  //     jsonpath: 'paidUnits',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'serviceRate',
  //     header: 'Service Rate',
  //     jsonpath: 'serviceRate',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'businessStatus',
  //     header: 'Business Status',
  //     jsonpath: 'businessStatus',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   },
  //   {
  //     field: 'processingStatus',
  //     header: 'Processing Status',
  //     jsonpath: 'processingStatus',
  //     isDisplayedAtHeader: 'y',
  //     isSearchable: 'n',
  //   }
  // ];

  // Handler for list-table view user events (clicking PPL IDs)
  onViewUserClicked = (payload: any) => {
    this.userService.setViewUserEvent({
      userId: payload.userId,
      isConsumer: payload.isConsumer
    });
  };

  fields = [
    // no serviceDate for purchase
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'serviceCode', show: true },
  ];

  keyMapping: { [key: string]: string } = {
    Timeframe: 'submissionDate',
    consumer: 'consumerName',
    consumerPplId: 'consumerPplId',
    serviceCode: 'serviceCodeDetailId'
  };

  constructor(
    private dateTimeFormatterService: DateTimeFormatterService,
    private errorAlertService: ErrorAlertService,
    private fb: FormBuilder,
    private listService: ListService,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService,
    private purchaseRequestService: PurchaseRequestService,
    private notificationService: NotificationService,
    private sectionsService: SectionsService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.businessStatusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.reasonList = this.lookupService.getLookupValue('Disposition Reason');
    this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');

    this.approvalTimesheetForm = this.fb.group({
      comments: [null]
    });

    this.setPageAccess();
  }

  private setPageAccess() {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }

  searchTimesheets(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20) {
    this.hasSearched = true;

    const adjustedTimesheetPayload = this.buildRequestPayload(
      'purchaseRequestAdjustmentEntry',
      'desc',
      filters,
      startingPageNumber,
      recordsPerPage
    );

    this.listService
      .getListData(adjustedTimesheetPayload, 'claims')
      .subscribe((data: any) => {
        const records = this.processData(data.responsedata);
        if (isChequeLookUp) {
          if (records && records?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
        else {
          if (startingPageNumber > 1) {
            this.timesheets = [...this.timesheets, ...records];
          } else {
            this.timesheets = records;
          }
          if (this.timesheets?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
      });
  }

  searchSubRowTimesheets(filters: any, event: any, startingPageNumber = 1, currentPage = 0) {
    const adjustedTimesheetPayload = this.buildRequestPayload(
      'purchaseRequestAdjustmentEntry',
      'purchaseRequestLineId',
      filters,
      startingPageNumber
    );

    this.listService
      .getListData(adjustedTimesheetPayload, 'claims')
      .subscribe((data: any) => {
        event.lineItems = data.responsedata;

        event?.lineItems?.forEach((element: any) => {
          element.timeInTimeOut = this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) + ' - ' + this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut),
            element.businessStatus = this.businessStatusList.filter((status: any) => status.lkpValueCode === element.businessStatusLkp)
              .map((status: any) => status.lkpValueName),
            element.processingStatus = this.processingStatusList.filter((status: any) => status.lkpValueCode === element.processingStatusLkp)
              .map((status: any) => status.lkpValueName)
        });

        event.showLineItem = true;
        event.loaded = true;
      });
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: [],
      orderBy: ['desc'],
    };

    return payload;
  }

  processData(data: any): any {
    if (data?.length > 0) {
      data.forEach((element: any) => {
        element.timeInTimeOut =
          this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) +
          ' - ' +
          this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut);

        element.businessStatus = this.businessStatusList
          .filter((s: any) => s.lkpValueCode === element.businessStatusLkp)
          .map((s: any) => s.lkpValueName);

        element.processingStatus = this.processingStatusList
          .filter((s: any) => s.lkpValueCode === element.processingStatusLkp)
          .map((s: any) => s.lkpValueName);

        const lookup = element.serviceCodeDetailId !== null && element.serviceCodeDetailId !== undefined
          ? this.serviceCodes.find(
              (sc: LookupValueItem) => sc.lkpValueCode === String(element.serviceCodeDetailId)
            )
          : undefined;
        if (lookup?.lkpValueName) {
          element.serviceCode = lookup.lkpValueName;
        } else {
          element.serviceCode = `${element.serviceCode ?? ''}: ${element.serviceDescription ?? ''}`;
        }

        element.viewOriginal = 'View Original';
      });
    }
    return data;
  }

  onSearch(event: any): void {
    const filters: any[] = [];

    if (event.Timeframe) {
      filters.push({ jsonPath: 'submissionDate', value: event.Timeframe, operator: 'greaterThan' });
    }
    if (event.submissionDate) {
      filters.push({
        jsonPath: 'submissionDate',
        value: this.dateTimeFormatterService.formatDateToString(event.submissionDate as Date),
        operator: 'Equal'
      });
    }
    if (event.pplPurchaseId?.trim()) {
      filters.push({ jsonPath: 'pplPurchaseId', value: event.pplPurchaseId.trim(), operator: 'Equal' });
    }
    if (event.consumer?.trim()) {
      filters.push({ jsonPath: 'consumerName', value: event.consumer.trim(), operator: 'equal' });
    }
    if (event.consumerPplId?.trim()) {
      filters.push({ jsonPath: 'consumerPplId', value: event.consumerPplId.trim(), operator: 'Equal' });
    }
    if (event.serviceCode) {
      filters.push({ jsonPath: 'serviceCodeDetailId', value: event.serviceCode, operator: 'Equal' });
    }

    filters.push({
      jsonPath: "processingStatusLkp",
      values: ['VI', 'TBP'],
      operator: "IN"
    });

    filters.push({ jsonPath: "approval", value: true, operator: "Equal" });

    this.appliedFilters = filters;
    this.searchTimesheets(filters, undefined, undefined, event.chequeLookUp, this.childComponent?.recordsPerPage ?? 20);
  }

  onPageLoad(event: any): void {
    this.searchTimesheets(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
  }

  openWarningMessage() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      showOkButton: true,
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.searchFilterWarningDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  onCellClicked(event: any) {
    const col = event?.col;
    const rowData = event?.rowData;
    if (col?.field === 'tasAuthorizationDetailId' && rowData?.tasAuthorizationDetailId) {
      this.openAuthDialog(rowData);
    } else if (col?.field === 'viewOriginal') {
      this.openOriginalRequestDialog(rowData);
    }
  }

  openOriginalRequestDialog(rowData: any) {
    const lineDetailId = rowData?.purchaseRequestLineId
      ?? rowData?.tasPurchaserequestLineDetailId
      ?? rowData?.tasPurchaseRequestLineDetailId;

    if (!lineDetailId) {
      this.errorAlertService.openModal('error', 'Error', 'Unable to determine the purchase request line to load the original request.');
      return;
    }

    this.originalRequestLines = [];
    this.originalRequestLoaded = false;

    const dialogConfig: DialogConfig = {
      title: 'Original Purchase Request',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false
    };

    this.originalRequestDialogId = this.dialogSvc.openDialog(
      this.originalRequestDialog,
      dialogConfig,
      () => this.closeOriginalRequestDialog(),
      () => this.closeOriginalRequestDialog()
    );

    this.purchaseRequestService.getAdjustmentParentLines(this.programName, lineDetailId, 'PR')
      .subscribe({
        next: (data: any) => {
          this.originalRequestLines = this.mapOriginalRequestLines(data?.responsedata ?? []);
          this.originalRequestLoaded = true;
        },
        error: () => {
          this.originalRequestLoaded = true;
        }
      });
  }

  mapOriginalRequestLines(lines: any[]): any[] {
    return (lines || []).map((line: any) => ({
      pplPurchaseId: line.ppl_purchase_id,
      consumerName: line.consumer_name,
      vendorName: line.vendor_name,
      mcoName: line.mco_name,
      service: line.service,
      taxAmount: this.formatAmount(line.tax_amount),
      shippingAmount: this.formatAmount(line.shipping_amount),
      totalAmount: this.formatAmount(line.total_amount),
      businessStatus: this.businessStatusList?.filter((s: any) => s.lkpValueCode === line.business_status_lkp)
        .map((s: any) => s.lkpValueName)?.[0] ?? line.business_status_lkp,
      processingStatus: this.processingStatusList?.filter((s: any) => s.lkpValueCode === line.prcocessing_status_lkp)
        .map((s: any) => s.lkpValueName)?.[0] ?? line.prcocessing_status_lkp
    }));
  }

  formatAmount(value: any): string {
    const amount = parseFloat(value);
    return isNaN(amount) ? (value ?? '') : `$${amount.toFixed(2)}`;
  }

  closeOriginalRequestDialog(): void {
    if (this.originalRequestDialogId) {
      this.dialogSvc.closeDialog(this.originalRequestDialogId);
      this.originalRequestDialogId = null;
    }
    this.originalRequestLines = [];
    this.originalRequestLoaded = false;
  }

  openAuthDialog(rowData: any) {
    this.selectedAuthorizationId = rowData.tasAuthorizationDetailId;
    const dialogConfig: DialogConfig = {
      title: `Auth Details for ${rowData.tasAuthorizationDetailId}`,
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };
    this.dialogSvc.openDialog(this.authDialog, dialogConfig, () => { }, () => { });
  }

  onAction(event: any): void {
    if (event?.action === 'view') {
      const row = event?.data;
      this.viewPurchaseRequestData = {
        ...row,
        tasPurchaserequestDetailId: row?.tasPurchaserequestDetailId ?? row?.purchaseRequestDetailId
      };
      this.openViewPurchaseRequest();
    }
  }

  openViewPurchaseRequest(): void {
    this.mode = 'view';
    const dialogConfig: DialogConfig = {
      title: 'View Purchase Request',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false
    };

    this.viewPurchaseRequestDialogId = this.dialogSvc.openDialog(
      this.viewPurchaseRequestDialog,
      dialogConfig,
      () => this.closeViewPurchaseRequestDialog(),
      () => this.closeViewPurchaseRequestDialog()
    );
  }

  closeViewPurchaseRequestDialog(): void {
    if (this.viewPurchaseRequestDialogId) {
      this.dialogSvc.closeDialog(this.viewPurchaseRequestDialogId);
      this.viewPurchaseRequestDialogId = null;
      this.viewPurchaseRequestData = null;
    }
  }

  handleViewPurchaseRequestCancel(event: any): void {
    if (event?.isCloseTriggeredByDialog) {
      this.closeViewPurchaseRequestDialog();
    }
  }

  rowExpanded(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }

    const subRowFilter = [];
    subRowFilter.push({
      jsonPath: "parent_timesheetLineId",
      value: event.timesheetLineId,
      operator: "Equal"
    });

    this.searchSubRowTimesheets(subRowFilter, event);
  }

  areArraysEqual(a: any[], b: any[]): boolean {
    if (!Array.isArray(a) || !Array.isArray(b))
      return false;

    if (a.length !== b.length)
      return false;

    return a.every((val, i) => val === b[i]);
  }

  isFilterValid(timesheetFilterValues: any, searchCallbackFilterValues: any): boolean {
    const activeFilters = Object.keys(timesheetFilterValues).filter(key => {
      const value = timesheetFilterValues[key];
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });

    const userAppliedFilters = searchCallbackFilterValues.filter(
      (filter: any) => !['businessStatusLkp', 'processingStatusLkp', 'serviceDuration', 'approval'].includes(filter.jsonPath)
    );

    if (activeFilters.length !== userAppliedFilters.length) return false;

    return Object.entries(timesheetFilterValues)
      .filter(([_, value]) => value !== null && value !== '')
      .every(([key, value]: [any, any]) => {
        const mappedKey = this.keyMapping[key] || key;
        if (Array.isArray(value) && value.length === 0) return true;

        let comparisonValue: any;
        if (mappedKey === 'serviceDate' || (key === 'submissionDate' && value instanceof Date)) {
          comparisonValue = this.dateTimeFormatterService.formatDateToString(value as Date);
        } else if (mappedKey === 'serviceDuration') {
          comparisonValue = value.substr(0, value.indexOf(' '));
        } else {
          comparisonValue = value;
        }

        return searchCallbackFilterValues.some((filter: any) =>
          filter.jsonPath === mappedKey &&
          (Array.isArray(filter.value || filter.values)
            ? this.areArraysEqual(filter.values, comparisonValue)
            : typeof filter?.value === 'string'
              ? filter?.value?.trim() === comparisonValue?.trim()
              : filter?.value === comparisonValue)
        );
      });
  }

  gridAction(event: any) {
    if (this.isReadOnly) return;
    const { button, selectedCheckBox } = event;

    if (this.timesheets.length === 0) {
      const errorMessage = `No purchase requests are available. Kindly perform a search that returns records before performing this action.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (!this.isFilterValid(this.timesheetFilterChildComponent.purchaseFilterForm.value, this.appliedFilters)) {
      const errorMessage = `Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (this.appliedFilters.length === 0 && (button.buttonAction === 'approveAllTimesheet' || button.buttonAction === 'rejectAllTimesheets')) {
      const errorMessage = `${button.button_label} action requires selection of filters. Kindly apply atleast one filter and perform the search with updated filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox && selectedCheckBox.length === 0
      && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Kindly select checkbox and then perform this action.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox.length > 1 && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Approve / Reject operation can be performed on single timesheet at a time.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (button) {
      this.dialogData = { payload: {} };
      switch (button.buttonAction) {
        case 'approveTimesheet':
        case 'rejectTimesheet': {
          const selectedTimesheet = selectedCheckBox[0];
          this.dialogData.payload.purchaseRequestLineIds = [selectedTimesheet.purchaseRequestLineId];
          this.isDialogNoteDisplayed = false;
          break;
        }
        case 'approveAllTimesheet':
        case 'rejectAllTimesheets': {
          if (selectedCheckBox.length > 0) {
            this.dialogData.payload.purchaseRequestLineIds = selectedCheckBox.map((entry: any) => entry.purchaseRequestLineId);
            this.dialogNoteMessage = `Note: The ${button.button_label} action will be performed on all selected purchase request.`
          }
          this.isDialogNoteDisplayed = true;
          break;
        }
      }

      this.dialogData.button = button;
      this.dialogButtonLabel = button.button_label;
      this.title = `${this.source.trim().charAt(0).toUpperCase() + this.source.trim().slice(1)} Adjustment ${button.button_label}`;
      this.commentText = button.button_label.indexOf('Approve') > -1 ? 'Approval comment' : 'Rejection comment *';

      if (button.buttonAction === 'rejectTimesheet' || button.buttonAction === 'rejectAllTimesheets') {
        this.approvalTimesheetForm.get('comments')?.setValidators([Validators.required]);
      } else {
        this.approvalTimesheetForm.get('comments')?.setValidators(null);
      }
      this.approvalTimesheetForm.get('comments')?.setValue(null);
      this.approvalTimesheetForm.markAsUntouched();
      this.approvalTimesheetForm.get('comments')?.updateValueAndValidity();
      this.showActionPopup = true;
    }
  }

  onSave() {
    if (this.isReadOnly) return;
    if (this.approvalTimesheetForm.valid) {
      let placeholder = '';
      const urlEndpoint = ENDPOINTS.CLAIMS.PAYROLL_PURCHASE_REQUEST_DECISION;
      const payload = this.dialogData.payload;
      payload.comments = this.approvalTimesheetForm?.get('comments')?.value;

      switch (this.dialogData.button.buttonAction) {
        case 'approveTimesheet':
        case 'approveAllTimesheet': {
          payload.decision = 'A';
          placeholder = 'Purchase Request approved';
          break;
        }
        case 'rejectTimesheet':
        case 'rejectAllTimesheets': {
          payload.decision = 'R';
          placeholder = 'Purchase Request rejected';
          break;
        }
      }
      console.log(this.dialogData);
      if (urlEndpoint) {
        this.purchaseRequestService.adjustPurchaseRequest(urlEndpoint, this.programName, payload, placeholder)
          .subscribe({
            next: () => {
              this.dialogData = null;
              this.showActionPopup = false;
              this.approvalTimesheetForm.reset();
              if (this.childComponent) {
                this.childComponent.selectedCheckBoxValue = [];
                this.childComponent.selectedRow = [];
              }
              this.searchTimesheets(this.appliedFilters, undefined, undefined, false, this.childComponent?.recordsPerPage ?? 20);
            },
            error: (err) => {
              this.notificationService.error('Failed', 'Failed')
            }

          });
      }
    } else {
      this.approvalTimesheetForm.markAllAsTouched();
    }
  }

}
